import React from "react";
import styled from "styled-components";

/**
 * CLE-2315 — inline rule descriptions + worked-math examples for the MPDR
 * duplicate-CPT controls. Three states are displayed (one is always active):
 *
 *   - allow-duplicate (existing) → duplicate pays the rank-N MPDR discount
 *   - duplicate-not-paid (NEW)   → duplicate pays $0 (packaged with the first)
 *   - neither checked            → MPDR skips duplicates entirely, they pay 100%
 *
 * Styling mirrors the inpatient ruleset examples (`InpatientCalculationExamples`)
 * so the visual pattern is instantly familiar.
 *
 * Math uses a $200 illustrative T-code rate; values are intentionally round so the
 * formula is easy to read.
 */

const ExampleContainer = styled.div`
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 0.875rem 1rem;
  margin-top: 0.5rem;
`;

const StateLabel = styled.div`
  font-size: 0.8rem;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.5rem;
`;

const Description = styled.div`
  font-size: 0.875rem;
  line-height: 1.5;
  color: #495057;
  margin-bottom: 0.5rem;
`;

const FormulaLine = styled.div`
  font-family: monospace;
  font-size: 0.875rem;
  line-height: 1.6;
  color: #495057;
`;

const ResultLine = styled.div`
  font-family: monospace;
  font-size: 0.875rem;
  line-height: 1.6;
  color: #2e7d32;
  font-weight: 600;
  margin-top: 0.25rem;
`;

const Caveat = styled.div`
  font-size: 0.8rem;
  color: #856404;
  margin-top: 0.5rem;
  font-style: italic;
`;

export type MpdrDuplicateState =
  | "ALLOW_DUPLICATE"
  | "DUPLICATE_NOT_PAID"
  | "NEITHER";

interface IMpdrDuplicateCptExampleProps {
  state: MpdrDuplicateState;
}

/**
 * Single-state worked example. The form renders one of these next to whichever
 * checkbox is currently active (or "Neither" when both are off).
 */
export function MpdrDuplicateCptExample({
  state,
}: IMpdrDuplicateCptExampleProps) {
  if (state === "ALLOW_DUPLICATE") {
    return (
      <ExampleContainer>
        <StateLabel>Effective behavior · Allow Duplicate</StateLabel>
        <Description>
          When the same CPT is billed more than once, the second-and-later
          instances follow the normal MPDR rank-N discount (the rank-2 % is
          applied to the 2nd, rank-3 to the 3rd, and so on).
        </Description>
        <FormulaLine>2 × T1234 at $200 contracted, rank-2 = 50%</FormulaLine>
        <FormulaLine>1st @ 100% = $200 · 2nd @ 50% = $100</FormulaLine>
        <ResultLine>Total expected = $300</ResultLine>
      </ExampleContainer>
    );
  }
  if (state === "DUPLICATE_NOT_PAID") {
    return (
      <ExampleContainer>
        <StateLabel>Effective behavior · Duplicate Not Paid</StateLabel>
        <Description>
          When the same CPT is billed more than once, the second-and-later
          instances pay <strong>$0</strong> (packaged with the first instance).
          Use this for payers that treat duplicate codes as a single payment —
          typically T-code rulesets.
        </Description>
        <FormulaLine>2 × T1234 at $200 contracted</FormulaLine>
        <FormulaLine>1st @ 100% = $200 · 2nd packaged = $0</FormulaLine>
        <ResultLine>Total expected = $200</ResultLine>
      </ExampleContainer>
    );
  }
  // NEITHER
  return (
    <ExampleContainer>
      <StateLabel>Effective behavior · Neither checked</StateLabel>
      <Description>
        MPDR ignores duplicates entirely — every instance pays at 100% of the
        contracted rate. Almost always the wrong choice for T-codes; most payers
        either discount duplicates (Allow Duplicate) or zero them out (Duplicate
        Not Paid).
      </Description>
      <FormulaLine>2 × T1234 at $200 contracted</FormulaLine>
      <FormulaLine>1st @ 100% = $200 · 2nd @ 100% = $200</FormulaLine>
      <ResultLine>Total expected = $400</ResultLine>
      <Caveat>
        If your expected is reading high vs. what the payer actually pays on
        duplicate-CPT claims, you probably want Duplicate Not Paid.
      </Caveat>
    </ExampleContainer>
  );
}
